/**
 * Ask->executed baseline correction - SIL-1833.
 *
 * `sku_mcmp` medians are built from every comp regardless of `price_kind`
 * (mcmp.ts), so a `sku_mcmp` FMV baseline is in practice an ASK median wherever
 * the SKU's comps are asks. `v_ask_executed_calibration` (842) already measures
 * the class/generation gap and the customer drawer DISCLOSES it
 * (resolveAskExecutedCalibrationDisclosure). This module adds the optional,
 * versioned CORRECTION. It does not estimate anything: the multiplier comes
 * from the existing view via fetchAskExecutedCalibration.
 *
 * ## Versioning / default
 *
 * `FmvFormulaConfig.askCalibration` is OPTIONAL and ABSENT from the committed
 * default. Absent means `disclose_only` (today's behaviour). Because the key is
 * absent, it is not part of the config content hash, so merging this change
 * leaves `defaultFmvFormulaConfig.version` byte-identical. Turning the
 * correction on means adding the field to the committed config, which yields a
 * new `version` like any formula change (ADR-0006).
 *
 * ## Rule (mode = 'apply', sku_mcmp baseline only)
 *
 *  1. A SKU with >= executedCompFloor executed comps in the mcmp window is NOT
 *     corrected: its median already carries executed prices (reason
 *     'executed_comps_sufficient').
 *  2. No calibration row, or an unpublished one (n_pairs < the view's minimum
 *     of 8): baseline unchanged, reason 'no_calibration' / 'below_sample_floor'.
 *     `minPairs` can only RAISE the floor above the view's own.
 *  3. Otherwise baseline x multiplier, with the multiplier clamped to
 *     [minMultiplier, maxMultiplier]. Both bounds are <= 1: a correction may
 *     only haircut, never inflate a price.
 */

import type { Kysely } from 'kysely';
import type { DB as Database } from '../db.js';
import type { AskExecutedCalibrationRow } from '../fmv-resolution.js';

export type AskCalibrationMode = 'disclose_only' | 'apply';

export interface AskCalibrationConfig {
  mode: AskCalibrationMode;
  /** Minimum n_pairs to apply. The view's `published` flag (n_pairs >= 8) is always required too. */
  minPairs: number;
  /** Lower clamp on the applied multiplier, in (0, 1]. */
  minMultiplier: number;
  /** Upper clamp on the applied multiplier, in [minMultiplier, 1]. */
  maxMultiplier: number;
  /** Executed comps in the mcmp window at/above which the SKU is left uncorrected. */
  executedCompFloor: number;
}

/** Behaviour when the config carries no `askCalibration`: disclose only. */
export const DISCLOSE_ONLY_ASK_CALIBRATION: AskCalibrationConfig = {
  mode: 'disclose_only',
  minPairs: 8,
  minMultiplier: 0.7,
  maxMultiplier: 1,
  executedCompFloor: 5,
};

export type AskCalibrationReason =
  | 'disclose_only'
  | 'not_sku_mcmp'
  | 'executed_comps_sufficient'
  | 'no_calibration'
  | 'below_sample_floor'
  | 'applied'
  | 'applied_clamped';

export interface AskCalibrationOutcome {
  mode: AskCalibrationMode;
  applied: boolean;
  /** Multiplier actually applied to the baseline (1 when not applied). */
  multiplier: number;
  /** Raw multiplier from the calibration view, when a row was consulted. */
  rawMultiplier: number | null;
  reason: AskCalibrationReason;
}

export function resolveAskCalibrationConfig(config: {
  askCalibration?: AskCalibrationConfig;
}): AskCalibrationConfig {
  return config.askCalibration ?? DISCLOSE_ONLY_ASK_CALIBRATION;
}

/** Pure decision. `calibrationRow`/`executedCompCount` are only consulted in 'apply' mode. */
export function resolveAskCalibrationCorrection(input: {
  config: { askCalibration?: AskCalibrationConfig };
  baselinePriceSourceId: string;
  calibrationRow: AskExecutedCalibrationRow | null;
  executedCompCount: number;
}): AskCalibrationOutcome {
  const cfg = resolveAskCalibrationConfig(input.config);
  const none = (reason: AskCalibrationReason, raw: number | null = null): AskCalibrationOutcome => ({
    mode: cfg.mode,
    applied: false,
    multiplier: 1,
    rawMultiplier: raw,
    reason,
  });

  if (cfg.mode !== 'apply') return none('disclose_only');
  if (input.baselinePriceSourceId !== 'sku_mcmp') return none('not_sku_mcmp');
  if (input.executedCompCount >= cfg.executedCompFloor) return none('executed_comps_sufficient');

  const row = input.calibrationRow;
  if (!row) return none('no_calibration');
  if (!row.published || row.nPairs < cfg.minPairs) return none('below_sample_floor', row.multiplier);
  if (!Number.isFinite(row.multiplier) || row.multiplier <= 0) return none('no_calibration');

  const clamped = Math.min(cfg.maxMultiplier, Math.max(cfg.minMultiplier, row.multiplier));
  return {
    mode: cfg.mode,
    applied: true,
    multiplier: clamped,
    rawMultiplier: row.multiplier,
    reason: clamped === row.multiplier ? 'applied' : 'applied_clamped',
  };
}

/** Returns the invariant violation message, or null when valid. */
export function askCalibrationConfigViolation(c: AskCalibrationConfig): string | null {
  const dp4 = (n: number) => Number.isFinite(n) && Math.round(n * 10000) === n * 10000;
  if (c.mode !== 'disclose_only' && c.mode !== 'apply') return `unknown mode "${String(c.mode)}"`;
  if (!Number.isInteger(c.minPairs) || c.minPairs < 8) return 'minPairs must be an integer >= 8 (the view minimum)';
  if (!Number.isInteger(c.executedCompFloor) || c.executedCompFloor < 1) return 'executedCompFloor must be an integer >= 1';
  if (!dp4(c.minMultiplier) || !dp4(c.maxMultiplier)) return 'multiplier bounds exceed 4dp';
  if (!(c.minMultiplier > 0 && c.minMultiplier <= c.maxMultiplier && c.maxMultiplier <= 1)) {
    return `bounds must satisfy 0 < minMultiplier <= maxMultiplier <= 1 (got ${c.minMultiplier}/${c.maxMultiplier})`;
  }
  return null;
}

/**
 * Live executed comps for the SKU (and, for a master, its live children - the
 * same comp set mcmp.ts rolls into the master) inside mcmp's 30-day window.
 * Counted across condition bands: the 'executed_comps_sufficient' rule is a
 * SKU-level one.
 */
export async function fetchExecutedCompCount(db: Kysely<Database>, skuId: string, now: Date = new Date()): Promise<number> {
  const children = await db
    .selectFrom('sku')
    .select('id')
    .where('parentSkuId', '=', skuId)
    .where('retiredAt', 'is', null)
    .execute();
  const row = await db
    .selectFrom('marketComp')
    .select((eb) => eb.fn.countAll<string>().as('n'))
    .where('skuId', 'in', [skuId, ...children.map((c) => c.id)])
    .where('priceKind', '=', 'executed')
    .where('excludedAt', 'is', null)
    .where('observedAt', '>=', new Date(now.getTime() - 30 * 86_400_000))
    .executeTakeFirst();
  return Number(row?.n ?? 0);
}
